import React from 'react';
import ReactDOM from 'react-dom/client';
import { MsalProvider } from '@azure/msal-react';
import { PorscheDesignSystemProvider } from '@porsche-design-system/components-react';
import { EventType } from '@azure/msal-browser';
import { msalInstance } from './auth/msalConfig';
import { clearReauthGuard } from './auth/authFetch';
import App from './App';
import './styles/global.css';

// MSAL renews tokens silently by loading redirectUri (this app's origin) in a
// hidden iframe and reading the #code response from it. If our bundle runs there
// — initializing MSAL, clearing the hash, or rendering — the iframe URL changes
// and MSAL aborts with `block_iframe_reload`. So when we detect we're inside an
// iframe, do nothing at all and let the parent window read the response.
const inIframe = window.self !== window.top;

msalInstance.addEventCallback((event) => {
  if (
    (event.eventType === EventType.LOGIN_SUCCESS ||
      event.eventType === EventType.ACQUIRE_TOKEN_SUCCESS) &&
    event.payload
  ) {
    if (event.payload.account) {
      msalInstance.setActiveAccount(event.payload.account);
    }
  }
});

async function startApp() {
  await msalInstance.initialize();

  // If we landed here with a redirect-response hash (#code= or #error=), a
  // reauth round-trip just completed — clear the loop guard regardless of
  // whether MSAL successfully extracts tokens below, so the next auth attempt
  // isn't permanently blocked by a stale flag.
  const hash = window.location.hash || '';
  const hadRedirectResponse = hash.includes('code=') || hash.includes('error=');
  if (hadRedirectResponse) clearReauthGuard();

  try {
    const response = await msalInstance.handleRedirectPromise();
    if (response?.account) {
      msalInstance.setActiveAccount(response.account);
    }
    // Whether or not MSAL surfaced an account, drop the hash so the URL is clean.
    if (hadRedirectResponse && window.location.hash) {
      window.history.replaceState(null, '', window.location.pathname + window.location.search);
    }
  } catch (e) {
    console.error('[MSAL] handleRedirectPromise failed:', e);
    if (window.location.hash) {
      window.history.replaceState(null, '', window.location.pathname + window.location.search);
    }
  }

  if (!msalInstance.getActiveAccount()) {
    const accounts = msalInstance.getAllAccounts();
    if (accounts.length) {
      msalInstance.setActiveAccount(accounts[0]);
    }
  }

  const root = ReactDOM.createRoot(document.getElementById('root'));
  root.render(
    <PorscheDesignSystemProvider theme="light">
      <MsalProvider instance={msalInstance}>
        <App />
      </MsalProvider>
    </PorscheDesignSystemProvider>
  );
}

// Don't boot inside MSAL's silent-renewal iframe — see note above.
if (!inIframe) {
  startApp();
}
